// Option data for the campaign form: where a creative can appear, what types and
// audiences exist, and the limits the backend also enforces.

// Most a single campaign can carry (matches the backend cap).
export const MAX_IMAGES = 10;

// Longest the close button may be withheld. Catches a unit mistake, e.g. minutes
// typed into a seconds field. The backend clamps to the same value.
export const MAX_CLOSE_DELAY_MS = 5 * 60 * 1000;

// Popups always dismiss themselves, so the timing rules in the form apply only to
// the other types, where the close button is the user's only way out.
export const hasGuaranteedDismissal = (creativeType) =>
    String(creativeType || '').toLowerCase() === 'popup';

// Per-tab full-width banner slots (one per bottom-tab, mounted in MainLayout).
// The "_banner" suffix makes it obvious these enable a tab's banner.
export const BANNER_PLACEMENTS = [
    'home_banner',
    'markets_banner',
    'news_banner',
    'learning_banner',
    'scans_banner',
    'ideas_banner',
];

// Inline content slots (mounted within a screen's scrolling content).
export const INLINE_PLACEMENTS = [
    // Home tab sections.
    'home_top_services',
    'home_stock_screener',
    'home_latest_news',
    'home_risk_profiling',
    'home_bottom',
    // In-feed slots (position-controlled): News, Learning, Ideas, Scans feeds.
    'news_feed',
    'learning_feed',
    'ideas_feed',
    'scans_feed',
];

// Placements that support in-feed position/repeat controls.
export const FEED_PLACEMENTS = ['news_feed', 'learning_feed', 'ideas_feed', 'scans_feed'];

// 'post' was a duplicate of 'page' (both render creative_html in a WebView), so it
// is no longer offered. The app still renders any legacy 'post' rows unchanged.
export const CREATIVE_TYPES = ['banner', 'popup', 'carousel', 'video', 'page'];

export const TARGET_ACTIONS = ['open_contact_form', 'open_iframe', 'force_update', 'logout', 'none'];

// Audience groups an ad can target. The value is the user's role id
// (access_roles), matched server-side against the authenticated user's role.
// "all" shows to everyone; only end-user tiers are meaningful ad audiences.
export const TARGET_GROUPS = [
    { value: 'all', label: 'All users' },
    { value: '2', label: 'End User - Extended Access' },
    { value: '1', label: 'End User - Limited Access' },
];

// Orders the placement suggestions by the chosen creative type: when "banner"
// is selected, the per-tab banner slots are listed first so the admin sees the
// tab options that match; otherwise the inline slots lead.
export const placementOptionsFor = (creativeType) =>
    creativeType === 'banner'
        ? [...BANNER_PLACEMENTS, ...INLINE_PLACEMENTS]
        : [...INLINE_PLACEMENTS, ...BANNER_PLACEMENTS];

// Human metadata for each placement slug — the app AREA it belongs to (used as the
// <optgroup>), a plain-English label, and a one-line "where it appears" hint. The
// stored value is always the slug itself, so this is display-only and changes no
// data. New slugs added to the app just need a line here to get a friendly label.
export const PLACEMENT_META = {
    home_banner:         { group: 'Home',     label: 'Top banner',           desc: 'Full-width strip at the very top of the Home tab.' },
    home_top_services:   { group: 'Home',     label: 'Below Top Services',   desc: 'Inline slot under the Top Services row on Home.' },
    home_stock_screener: { group: 'Home',     label: 'Below Stock Screeners', desc: 'Inline slot under the Stock Screeners section on Home.' },
    home_latest_news:    { group: 'Home',     label: 'Below Latest News',    desc: 'Inline slot under the Latest News section on Home.' },
    home_risk_profiling: { group: 'Home',     label: 'Below Risk Profiling', desc: 'Inline slot under Personal Risk Profiling on Home.' },
    home_bottom:         { group: 'Home',     label: 'Bottom of Home',       desc: 'Inline slot at the very bottom of the Home tab.' },
    news_banner:         { group: 'News',     label: 'Top banner',           desc: 'Full-width strip at the top of the News tab.' },
    news_feed:           { group: 'News',     label: 'In-feed promo',        desc: 'Card woven between the news articles (position-controlled).' },
    learning_banner:     { group: 'Learning', label: 'Top banner',           desc: 'Full-width strip at the top of the Learning tab.' },
    learning_feed:       { group: 'Learning', label: 'In-feed promo',        desc: 'Card woven between the learning cards (position-controlled).' },
    ideas_banner:        { group: 'Ideas',    label: 'Top banner',           desc: 'Full-width strip at the top of the Ideas tab.' },
    ideas_feed:          { group: 'Ideas',    label: 'In-feed promo',        desc: 'Card woven between the idea cards (position-controlled).' },
    scans_banner:        { group: 'Scans',    label: 'Top banner',           desc: 'Full-width strip at the top of the Scans tab.' },
    scans_feed:          { group: 'Scans',    label: 'In-feed promo',        desc: 'Card woven between the scan cards (position-controlled).' },
    markets_banner:      { group: 'Markets',  label: 'Top banner',           desc: 'Full-width strip at the top of the Markets tab.' },
};

// Order the app areas appear in the dropdown.
export const PLACEMENT_GROUP_ORDER = ['Home', 'News', 'Learning', 'Ideas', 'Scans', 'Markets'];

// Builds the grouped option list for the placement <select>. Groups the available
// slugs (ordered by creative type) under their app area. Safety net: if the
// campaign being edited holds a slug that isn't in the known set (legacy/renamed),
// it is surfaced as its own "Current" option so opening + saving an old campaign
// never silently drops or changes its placement.
export const buildPlacementGroups = (creativeType, currentValue) => {
    const available = placementOptionsFor(creativeType);
    const known = new Set(available);
    const groups = PLACEMENT_GROUP_ORDER
        .map((area) => ({ group: area, items: available.filter((slug) => PLACEMENT_META[slug]?.group === area) }))
        .filter((g) => g.items.length);
    if (currentValue && !known.has(currentValue)) {
        groups.unshift({ group: 'Current', items: [currentValue], custom: true });
    }
    return groups;
};
